import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { NumberPlate, AUSTRALIAN_STATES } from "@/components/NumberPlate";
import { InstallAppButton } from "@/components/InstallAppButton";
import { ShieldCheck, UserCheck, EyeOff, Heart, Feather, Sparkles } from "lucide-react";

const HERO = "https://images.pexels.com/photos/5408346/pexels-photo-5408346.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=1000";
const IMG_LETTER = "https://images.pexels.com/photos/6053/man-hands-reading-boy.jpg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_HANDS = "https://images.pexels.com/photos/1024960/pexels-photo-1024960.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  const nav = useNavigate();
  const pickState = (state) => nav(`/register?state=${state}`);
  return (
    <div className="relative paper">
      {/* NAV */}
      <header className="sticky top-0 z-40" style={{ background: "rgba(242,235,216,0.85)", backdropFilter: "blur(12px)", borderBottom: "1px solid rgba(14,18,20,0.08)" }}>
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <Link to="/" data-testid="landing-logo"><Logo /></Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-ink/80">
            <a href="#how" data-testid="link-how">How it works</a>
            <a href="#vouched" data-testid="link-vouched">The Vouch</a>
            <a href="#depth" data-testid="link-depth">Blind Depth</a>
            <Link to="/premium" data-testid="link-premium">Premium</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="landing-signin" className="px-4 py-2 text-sm text-ink rounded-full hover:bg-white/60 transition-all duration-500">Sign in</Link>
            <Link to="/register" data-testid="landing-getstarted" className="btn-pill bg-ink text-parchment text-sm hover:bg-ivy-500 hover:scale-[1.02]">Request your license</Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-8 pt-20 pb-24 grid md:grid-cols-[1.15fr_0.85fr] gap-14 items-center">
        <div className="reveal">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border" style={{ borderColor: "#D4A574", background: "rgba(212,165,116,0.12)" }}>
            <ShieldCheck size={14} className="text-ochre-300" />
            <span className="eyebrow text-ochre-300" style={{ letterSpacing: "0.24em" }}>By invitation of the people who love you</span>
          </div>
          <h1 className="font-display mt-8 text-ink" style={{ fontSize: "5.5rem", lineHeight: 1.0, fontWeight: 400 }}>
            Get your<br/>
            <em className="italic text-ivy" style={{ fontFamily: "Fraunces" }}>License</em> to Love.
          </h1>
          <p className="mt-8 text-lg text-ink/70 max-w-xl leading-relaxed">
            Every other dating app lets anyone in. Wholesome asks something different: <strong className="text-ink">bring three people who love you</strong> — a friend, a sibling, a colleague — and let <em>them</em> vouch for the person you actually are.
          </p>
          <p className="mt-4 text-lg text-ink/70 max-w-xl leading-relaxed">
            Once vouched, you receive your License to Love. And a very different kind of dating begins.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link to="/register" data-testid="hero-cta-primary" className="btn-pill bg-ivy text-parchment hover:bg-ivy-600 shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all">
              Begin your application
            </Link>
            <Link to="/login" data-testid="hero-cta-secondary" className="btn-pill bg-white/70 text-ink border border-ink/10 hover:bg-white">
              I've been invited
            </Link>
          </div>
          <div className="mt-10 flex items-center gap-8 text-xs text-ink/60">
            <div className="flex items-center gap-2"><UserCheck size={14} className="text-ivy" /> 3-person character verification</div>
            <div className="flex items-center gap-2"><EyeOff size={14} className="text-ivy" /> 7 days before you see a face</div>
            <div className="flex items-center gap-2"><Heart size={14} className="text-ochre-300" /> A kindness that grows</div>
          </div>
        </div>
        <div className="relative reveal" style={{ animationDelay: "150ms" }}>
          <div className="absolute -inset-4 rounded-[2.5rem] blur-2xl" style={{ background: "linear-gradient(135deg, rgba(212,165,116,0.35), rgba(46,74,59,0.2))" }} />
          <div className="relative rounded-[1.5rem] overflow-hidden shadow-2xl border-2" style={{ borderColor: "#D4A574" }}>
            <img src={HERO} alt="" className="w-full h-[560px] object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(14,18,20,0.6), transparent 50%)" }} />
            {/* Seal overlay */}
            <div className="absolute bottom-6 left-6 right-6 flex items-end gap-4">
              <div className="seal shrink-0 text-center leading-tight">
                <span>Vouched<br/><em>Member</em></span>
              </div>
              <div className="text-parchment">
                <div className="font-display italic" style={{ fontFamily: "Fraunces", fontSize: 22 }}>Emma & Noah</div>
                <div className="text-xs opacity-80 mt-1" style={{ fontFamily: "JetBrains Mono" }}>LTL-A3F71C · Ballarat, VIC · Vouched by 6</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PICK YOUR STATE — the number plates */}
      <section id="states" className="max-w-7xl mx-auto px-8 pt-4 pb-24" data-testid="states-section">
        <div className="max-w-3xl">
          <div className="eyebrow text-ochre-300">Where's your heart parked?</div>
          <h2 className="font-display text-5xl md:text-6xl mt-4 text-ink" style={{ fontWeight: 400 }}>
            Pick your <em className="italic text-ivy">plate.</em>
          </h2>
          <p className="mt-4 text-lg text-ink/70 max-w-xl leading-relaxed">
            Tap the state you call home to begin your application. Your License to Love travels with you — but every plate begins somewhere.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 justify-items-center">
          {AUSTRALIAN_STATES.map((s, i) => (
            <div
              key={s}
              className="reveal"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <NumberPlate state={s} plate={`LTL·${s}`} onClick={() => pickState(s)} size="md" />
            </div>
          ))}
        </div>

        <div className="mt-10 text-center text-xs text-ink/50 font-mono tracking-widest">
          NOT IN AUSTRALIA? <Link to="/register" className="text-ivy hover:underline" data-testid="states-skip">SKIP AHEAD →</Link>
        </div>
      </section>

      {/* THE VOUCH */}
      <section id="vouched" className="max-w-7xl mx-auto px-8 py-24">
        <div className="max-w-3xl">
          <div className="eyebrow">The vouch · 01</div>
          <h2 className="font-display text-6xl mt-4 text-ink" style={{ fontWeight: 400 }}>
            You can't join Wholesome <em className="italic text-ivy">alone.</em>
          </h2>
          <p className="mt-6 text-lg text-ink/70 leading-relaxed max-w-2xl">
            Ask three people who know you well — a mum, a sister, a best mate, a colleague — to answer one question about you on video: <em>"Why would this person make a wonderful partner?"</em> Their words become part of your profile. Your community writes your bio.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-16">
          {[
            { n: "01", t: "Request three vouchers", d: "During signup, add three people who love you. We send them a private link — no account needed." },
            { n: "02", t: "They record a 2-min vouch", d: "One question. One story. On video. Their words are your bio." },
            { n: "03", t: "Your License is issued", d: "One vouch confirmed and you're in. Reach three within 14 days and you're fully Licensed." },
          ].map((p) => (
            <div key={p.n} className="card-paper p-8 hover:-translate-y-1 transition-all duration-500">
              <div className="text-4xl font-display text-amber-300 italic" style={{ fontFamily: "Fraunces" }}>{p.n}</div>
              <h3 className="font-display text-2xl text-ink mt-4">{p.t}</h3>
              <p className="mt-2 text-sm text-ink/70 leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* THE CERTIFICATE MOCKUP */}
      <section className="max-w-6xl mx-auto px-8 py-16">
        <div className="relative rounded-2xl p-16 shadow-2xl paper" style={{ border: "2px double #D4A574" }}>
          <div className="absolute top-6 left-6 eyebrow text-ochre-300">Wholesome™</div>
          <div className="absolute top-6 right-6 text-xs" style={{ fontFamily: "JetBrains Mono" }}>No. LTL-A3F71C</div>
          <div className="text-center ornament eyebrow text-ivy-500">Certificate of Character</div>
          <h3 className="text-center font-display italic mt-4 text-ink" style={{ fontFamily: "Fraunces", fontSize: 62, fontWeight: 400 }}>License to Love</h3>
          <div className="text-center mt-6 text-ink/70 text-lg max-w-2xl mx-auto leading-relaxed">
            This is to certify that <span className="italic font-display text-ivy" style={{ fontFamily: "Fraunces", fontSize: 28 }}>Emma Grace Wilson</span> has been vouched for as a kind and genuine person by three humans who know her well.
          </div>
          <div className="flex justify-center gap-8 mt-10 text-xs" style={{ fontFamily: "JetBrains Mono" }}>
            <div><div className="text-ochre-300 mb-1">VOUCHED BY</div><div>Sarah W. · sister</div></div>
            <div><div className="text-ochre-300 mb-1">VOUCHED BY</div><div>James M. · friend, 12y</div></div>
            <div><div className="text-ochre-300 mb-1">VOUCHED BY</div><div>Priya N. · colleague</div></div>
          </div>
          <div className="flex items-center justify-between mt-10">
            <div className="text-xs text-ink/60" style={{ fontFamily: "JetBrains Mono" }}>Issued · Ballarat, VIC · 22 July 2026</div>
            <div className="seal">Character<br/><em>Verified</em></div>
            <div className="text-xs text-ink/60 text-right" style={{ fontFamily: "JetBrains Mono" }}>Wholesome™<br/>Registrar of Kindness</div>
          </div>
        </div>
      </section>

      {/* BLIND DEPTH */}
      <section id="depth" className="max-w-7xl mx-auto px-8 py-24">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div className="rounded-[1.5rem] overflow-hidden shadow-2xl border" style={{ borderColor: "rgba(14,18,20,0.1)" }}>
            <img src={IMG_LETTER} alt="Letters" className="w-full h-[540px] object-cover" />
          </div>
          <div>
            <div className="eyebrow">Blind depth · 02</div>
            <h2 className="font-display text-6xl mt-4 text-ink" style={{ fontWeight: 400 }}>
              Seven days<br/>before you <em className="italic text-ivy">see them.</em>
            </h2>
            <p className="mt-6 text-lg text-ink/70 leading-relaxed">
              A match is a beginning, not a photo. For the first seven days, no pictures. No video. Just one thoughtful prompt each morning and a private thread to answer it. Voice notes from day three.
            </p>
            <p className="mt-4 text-lg text-ink/70 leading-relaxed">
              On day eight, the reveal. By then you already care whether the face matches the mind.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-3">
              {[["Day 1", "The prompt"], ["Day 3", "Voice"], ["Day 8", "The reveal"]].map(([d, l]) => (
                <div key={d} className="card-paper p-5">
                  <div className="text-xs text-ochre-300 font-mono">{d.toUpperCase()}</div>
                  <div className="font-display text-2xl text-ink mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* KIND LEDGER */}
      <section className="max-w-7xl mx-auto px-8 py-24">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="eyebrow">The kind ledger · 03</div>
            <h2 className="font-display text-6xl mt-4 text-ink" style={{ fontWeight: 400 }}>
              A profile that <em className="italic text-ivy">writes itself.</em>
            </h2>
            <p className="mt-6 text-lg text-ink/70 leading-relaxed">
              Every gratitude entry, every kind message the recipient chose to save, every moment shared, every friend vouched for — quietly logged into your Kind Ledger. Not gamified points. A slowly-forming portrait of who you are, verified by the community around you.
            </p>
            <p className="mt-4 text-lg text-ink/70 leading-relaxed">
              Bios lie. Ledgers don't.
            </p>
          </div>
          <div className="rounded-[1.5rem] overflow-hidden shadow-2xl border" style={{ borderColor: "rgba(14,18,20,0.1)" }}>
            <img src={IMG_HANDS} alt="" className="w-full h-[540px] object-cover" />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS TIMELINE */}
      <section id="how" className="max-w-7xl mx-auto px-8 py-24">
        <div className="text-center max-w-3xl mx-auto">
          <div className="eyebrow">How it works</div>
          <h2 className="font-display text-6xl mt-4 text-ink" style={{ fontWeight: 400 }}>Four gentle steps.</h2>
        </div>
        <div className="grid md:grid-cols-4 gap-6 mt-16">
          {[
            [Feather, "Apply", "Two minutes. No swiping. Add the three people who'll vouch for you."],
            [UserCheck, "Get vouched", "They record one story. You receive your License to Love."],
            [EyeOff, "Blind depth", "7 days of prompts before you see each other."],
            [Heart, "Grow together", "Communities, gratitude, learning — a life built beside someone kind."],
          ].map(([Ic, t, d], i) => (
            <div key={i} className="text-center">
              <div className="w-16 h-16 rounded-full bg-ivy/10 text-ivy inline-flex items-center justify-center mb-4">
                <Ic size={24} />
              </div>
              <div className="font-display text-2xl text-ink">{t}</div>
              <div className="text-sm text-ink/60 mt-2 max-w-[220px] mx-auto">{d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CLOSING */}
      <section className="max-w-4xl mx-auto px-8 py-32 text-center">
        <h2 className="font-display text-6xl md:text-7xl text-ink leading-tight" style={{ fontWeight: 400 }}>
          Every other app asks<br/><em className="italic text-ivy">who you look like.</em><br/>
          <span className="text-ochre-300">We ask who loves you.</span>
        </h2>
        <div className="mt-12 flex items-center justify-center gap-3">
          <Link to="/register" data-testid="cta-register" className="btn-pill bg-ivy text-parchment hover:bg-ivy-600 hover:scale-[1.02] text-lg px-10 py-4">
            Request your License
          </Link>
        </div>
        <div className="font-script text-4xl mt-12 text-ochre-300" style={{ fontFamily: "Caveat" }}>The people who love you already know.</div>
      </section>

      <footer className="border-t border-ink/10 py-8">
        <div className="max-w-7xl mx-auto px-8 flex flex-wrap items-center justify-between gap-3 text-xs text-ink/60">
          <Logo size={22} />
          <InstallAppButton label="Install the app" />
          <div style={{ fontFamily: "JetBrains Mono" }}>© 2026 WHOLESOME™ · LICENSE TO LOVE · REGISTERED IN BALLARAT, VIC</div>
        </div>
      </footer>
    </div>
  );
}
